import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, ClipboardCheck, Users, ArrowRight } from "lucide-react";
import apiClient from "../../utils/apiClient";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); NewForYouModal.scss deleted with it.
//
// The hero and the CTA were `linear-gradient(135deg, hsl(258 80% 60%), hsl(220 85% 58%))`
// — a violet and a blue written as raw HSL, so this promo was the ONE surface in
// the student dashboard that could not follow the institute's brand. It is
// --primary → --chart-1 now, the pair TwoFactorModal's badge already uses.
//
// The CTA deliberately stays a plain <button> rather than becoming AppButton: a
// gradient promo button is not one of AppButton's five variants, and the fix for
// "a module grew its own button" is to use the shared one where the accent was
// the only difference — which is not the case here. Its ink is
// --primary-solid-fg, not #fff, for the Phase 7.1 reason: a light institute
// brand makes white unreadable.
const HERO_GRADIENT = "tw-bg-gradient-to-br tw-from-primary-solid tw-to-chart-1";

const CLOSE =
  "tw-absolute tw-right-[0.6rem] tw-top-[0.6rem] tw-z-[2] tw-inline-flex tw-h-[30px] " +
  "tw-w-[30px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full " +
  "tw-border-0 tw-bg-primary-solid-fg/[0.18] tw-text-primary-solid-fg hover:tw-bg-primary-solid-fg/30";

const ITEM =
  "tw-flex tw-cursor-pointer tw-items-center tw-gap-[0.7rem] tw-rounded-[0.7rem] " +
  "tw-border tw-border-line tw-px-[0.7rem] tw-py-[0.65rem] " +
  "tw-transition-[border-color,background-color] tw-duration-150 " +
  "hover:tw-border-primary/50 hover:tw-bg-primary/5";

const ITEM_ICON =
  "tw-inline-flex tw-h-[34px] tw-w-[34px] tw-shrink-0 tw-items-center tw-justify-center " +
  "tw-rounded-[0.6rem] tw-bg-primary/[0.12] tw-text-primary";

const CTA =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-[0.4rem] " +
  "tw-rounded-[0.7rem] tw-border-0 tw-px-4 tw-py-[0.7rem] tw-text-[0.92rem] tw-font-bold " +
  "tw-text-primary-solid-fg hover:tw-brightness-105 " + HERO_GRADIENT;

// Shown at most ONCE per browser session (not on every dashboard navigation).
const SESSION_KEY = "vc-newforyou-shown";
const RELEVANT = new Set(["assessment_assigned", "batch_assigned"]);

const iconFor = (type) => (type === "batch_assigned" ? Users : ClipboardCheck);

/**
 * NewForYouModal — a promo-style "new for you" popup for students, surfaced once
 * per session when a new assessment has been assigned to their batch (or they
 * were added to a new batch). Reads unread notifications on mount and also reacts
 * to live socket pushes (`vc-notification`). Lives in the student dashboard.
 */
const NewForYouModal = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const showOnce = useCallback((list) => {
    if (!list || list.length === 0) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    sessionStorage.setItem(SESSION_KEY, "1");
    setItems(list.slice(0, 5));
    setOpen(true);
  }, []);

  // On mount — pull any unread assessment/batch notifications (once per session).
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return undefined;
    let active = true;
    apiClient
      .get("/notifications", { params: { unreadOnly: true, pageSize: 10 } })
      .then(({ data }) => {
        if (!active) return;
        const relevant = (data.notifications || []).filter((n) => RELEVANT.has(n.type));
        showOnce(relevant);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [showOnce]);

  // Live — a socket notification arrived while the app is open.
  useEffect(() => {
    const onNotif = (e) => {
      const d = e.detail || {};
      if (!RELEVANT.has(d.type)) return;
      showOnce([{ type: d.type, title: d.title, message: d.message, link: d.data?.url }]);
    };
    window.addEventListener("vc-notification", onNotif);
    return () => window.removeEventListener("vc-notification", onNotif);
  }, [showOnce]);

  if (!open) return null;

  const go = (link) => { setOpen(false); if (link) navigate(link); };
  const primary = items[0];

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-z-modal tw-flex tw-items-center tw-justify-center tw-bg-[var(--overlay-bg)] tw-p-4 tw-animate-panel-fade"
      onClick={() => setOpen(false)}
    >
      <div
        className="tw-relative tw-w-full tw-max-w-[440px] tw-overflow-hidden tw-rounded-lg tw-bg-surface-raised tw-shadow-popover tw-animate-slide-up"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button className={CLOSE} onClick={() => setOpen(false)} aria-label="Close"><X size={18} /></button>

        <div className={`${HERO_GRADIENT} tw-px-5 tw-pb-[1.35rem] tw-pt-[1.6rem] tw-text-center tw-text-primary-solid-fg`}>
          <span className="tw-mb-[0.55rem] tw-inline-flex tw-h-[52px] tw-w-[52px] tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-solid-fg/[0.18]"><Sparkles size={26} /></span>
          <h2 className="tw-m-0 tw-text-[1.35rem] tw-font-extrabold tw-tracking-[-0.01em]">New for you!</h2>
          <p className="tw-mb-0 tw-mt-[0.2rem] tw-text-[0.86rem] tw-opacity-[0.92]">
            {items.length === 1 ? "You have a new update" : `You have ${items.length} new updates`}
          </p>
        </div>

        <ul className="tw-m-0 tw-flex tw-max-h-[46vh] tw-list-none tw-flex-col tw-gap-2 tw-overflow-y-auto tw-px-4 tw-pb-[0.4rem] tw-pt-[0.85rem]">
          {items.map((n, i) => {
            const Icon = iconFor(n.type);
            return (
              <li key={i} className={ITEM} onClick={() => go(n.link)} role="button" tabIndex={0}>
                <span className={ITEM_ICON}><Icon size={16} /></span>
                <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                  <span className="tw-text-[0.88rem] tw-font-bold tw-text-content">{n.title}</span>
                  {n.message && (
                    <span className="tw-truncate tw-text-[0.78rem] tw-text-muted-fg">{n.message}</span>
                  )}
                </span>
                <ArrowRight size={15} className="tw-shrink-0 tw-text-muted-fg" />
              </li>
            );
          })}
        </ul>

        <div className="tw-flex tw-flex-col tw-gap-2 tw-px-4 tw-pb-[1.1rem] tw-pt-[0.6rem]">
          <button className={CTA} onClick={() => go(primary?.link || "/s/assessments")}>
            View now <ArrowRight size={16} />
          </button>
          <button
            className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-[0.2rem] tw-text-[0.82rem] tw-font-semibold tw-text-muted-fg hover:tw-text-content"
            onClick={() => setOpen(false)}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NewForYouModal;
